/**
 * Apple devices play HLS natively (with AirPlay and captions in their fullscreen player). Every browser on iOS uses
 * Apple's engine. Other browsers get hls.js, loaded only when a video is played, to keep it out of the page bundle.
 * Android's Chrome also claims native HLS support, but handles alternate audio and subtitles unreliably.
 */
export function prefersNativeHls(userAgent = navigator.userAgent, maxTouchPoints = navigator.maxTouchPoints): boolean {
  const isIos = /iPhone|iPad|iPod/.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1)
  const isDesktopSafari = /Safari/.test(userAgent) && !/Chrome|Chromium|CriOS|Edg|Firefox|FxiOS|OPR|Android/.test(userAgent)

  return isIos || isDesktopSafari
}

/** Starts loading the stream into the video element. Returns a function that releases it. */
export async function attachStream(video: HTMLVideoElement, url: string): Promise<() => void> {
  if (prefersNativeHls()) return attachNatively(video, url)

  const { default: Hls } = await import('hls.js')
  if (!Hls.isSupported()) return attachNatively(video, url)

  // Captions are selected here, not marked as default in the master playlists, so already packaged streams need no rebuild.
  const hls = new Hls({ capLevelToPlayerSize: true, subtitlePreference: { groupId: 'subtitles' } })
  hls.loadSource(url)
  hls.attachMedia(video)
  return () => hls.destroy()
}

function attachNatively(video: HTMLVideoElement, url: string): () => void {
  const showCaptions = ({ track }: TrackEvent) => {
    if (track instanceof TextTrack && track.kind === 'subtitles') track.mode = 'showing'
  }
  video.textTracks.addEventListener('addtrack', showCaptions)
  video.src = url

  return () => {
    video.textTracks.removeEventListener('addtrack', showCaptions)
    video.removeAttribute('src')
  }
}
